import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useShallow } from 'zustand/react/shallow';

import { useNotificationsHandler, useRoomQuery, useWebinarSocketListener } from '@src/hooks';
import useSendPageFinished from '@src/hooks/useSendPageFinished';

import SocketService from '@src/services/SocketService';
import { fetchMagicPenAnnotations } from '@src/components/MagicPen/services';

import { getRemainingTime, isWebView } from '@src/helpers';

import { useRoomStore, useWebinarRoomStore } from '@src/stores';

import { EWebinarRoutes, EWebinarSockets } from '@src/constants';
import { EUserType, EWebinarActiveStatus, IAttendeeDetailsApiResponse } from '@src/types';

const useWebinarJoin = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [haveActiveTimer, setHaveActiveTimer] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const webinarId = searchParams.get('wid');
  const userId = searchParams.get('uid');
  const titleParam = searchParams.get('title');

  useNotificationsHandler();
  useWebinarSocketListener();
  useSendPageFinished({ isMeetingEnded: false });

  const { data: webinarDetails } = useRoomQuery(webinarId ?? '');

  const { isJoinedRoom, setRoomTitle } = useRoomStore(
    useShallow((state) => ({
      isJoinedRoom: state.isJoinedRoom,
      setRoomTitle: state.setRoomTitle,
    }))
  );

  const { attendeeDetails, isWebinarStartedByHost, setIsWebinarStartedByHost } = useWebinarRoomStore(
    useShallow((state) => ({
      attendeeDetails: state.attendeeDetails,
      isWebinarStartedByHost: state.isWebinarStartedByHost,
      setIsWebinarStartedByHost: state.setIsWebinarStartedByHost,
    }))
  );

  const { isHost, isAttendee } = useMemo(() => {
    const userType = attendeeDetails?.user?.type;
    return {
      isHost: userType === EUserType.HOST,
      isAttendee:
        (!!attendeeDetails?.user && attendeeDetails?.user?.type === undefined) ||
        attendeeDetails?.user?.type === EUserType.ATTENDEE,
    };
  }, [attendeeDetails]);

  useEffect(() => {
    if (webinarId) {
      useWebinarRoomStore.setState({ webinarId });

      localStorage.setItem('userId', userId || '');

      localStorage.setItem('lastRoom', webinarId || '');
      localStorage.setItem('hasJoined', 'true');

      // Store the title if provided in URL params and in WebView
      if (isWebView() && titleParam) {
        localStorage.setItem('lastRoomTitle', decodeURIComponent(titleParam));
      }

      const socketUrl = process.env.REACT_APP_WEBINAR_SOCKET_URL;
      if (!socketUrl) {
        throw new Error('REACT_APP_WEBINAR_SOCKET_URL is not defined');
      }

      SocketService.connect(socketUrl, {
        transports: ['websocket'],
        withCredentials: true,
        query: { room_id: webinarId },
      });
    }

    return () => {
      if (SocketService.getSocketConnectionState()) {
        SocketService.disconnect();
      }
    };
  }, [userId, webinarId, titleParam]);

  // Global browser network diagnostics for webinar flow
  useEffect(() => {
    const handleOnline = () => console.log('[Network] online');
    const handleOffline = () => console.warn('[Network] offline');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Set title from URL parameter if available and in WebView
    if (isWebView() && titleParam) {
      setRoomTitle(decodeURIComponent(titleParam));
    }

    SocketService.request(EWebinarSockets.ATTENDEE_DETAILS, {
      roomId: webinarId,
      userId,
    }).then((data: IAttendeeDetailsApiResponse) => {
      if (data?.body?.user) {
        useWebinarRoomStore.getState().setAttendeeDetails(data.body);

        if (data.body?.hasAnnotations) {
          void fetchMagicPenAnnotations();
        }

        if (data.body.user.status === 'REJECTED') {
          navigate(EWebinarRoutes.WEBINAR_EXPIRED);
        }
      } else {
        if (!data.success) {
          const errorCode = data.errorCode ?? '';
          if (errorCode === 'USER_NOT_FOUND') {
            navigate(EWebinarRoutes.WEBINAR_EXPIRED);
          } else if (errorCode === 'ROOM_NOT_FOUND') {
            navigate(EWebinarRoutes.ROOM_NOT_FOUND);
          }
        }
        console.warn('Join webinar response:', data);
      }
      // Only set room title from API if no title parameter was provided
      if (!titleParam && data?.body?.roomTitle) {
        setRoomTitle(data.body.roomTitle);
        // Also store it in localStorage for rejoin if in WebView
        if (isWebView()) {
          localStorage.setItem('lastRoomTitle', data.body.roomTitle);
        }
      }
    });
  }, [webinarId, userId, setRoomTitle, navigate, titleParam]);

  useEffect(() => {
    if (!webinarDetails || !attendeeDetails) return;

    const { startTime, status } = webinarDetails;

    // If webinar is INACTIVE/ended, redirect immediately
    if (status === EWebinarActiveStatus.INACTIVE) {
      navigate(EWebinarRoutes.END, { state: { roomType: 'webinar' }, replace: true });
      return;
    }
    if (status === EWebinarActiveStatus.ACTIVE) {
      setIsWebinarStartedByHost(true);
      setIsLoading(false);
      return;
    }

    // Setup countdown if webinar hasn't started yet
    if (startTime) {
      const { ended } = getRemainingTime(new Date(startTime));
      if (!ended) {
        setHaveActiveTimer(true);
      }
    }

    setIsLoading(false);
  }, [webinarDetails, navigate, setIsWebinarStartedByHost, attendeeDetails]);

  /**
   * This useEffect checks the time remaining until the webinar's start.
   * When the remaining time becomes 20 minutes or less, it emits the "webinarLobby" socket event
   * to notify the backend that the attendee should enter the lobby room and wait for the host to start.
   */
  useEffect(() => {
    if (
      !webinarId ||
      !attendeeDetails ||
      !webinarDetails?.startTime ||
      webinarDetails.status === EWebinarActiveStatus.ACTIVE
    ) {
      return;
    }

    const startTime = new Date(webinarDetails.startTime).getTime();
    const now = Date.now();
    const twentyMinutes = 20 * 60 * 1000; // 20 minutes in milliseconds

    // Calculate how long to wait until it's 20 minutes before the webinar
    const waitTime = startTime - twentyMinutes - now;

    const handleActionPerUserType = () => {
      if (isHost) {
        setHaveActiveTimer(false);
      } else {
        SocketService.request(EWebinarSockets.JOIN_LOBBY, { roomId: webinarId, userId });
      }
    };

    if (waitTime <= 0) {
      // Already less than 20 minutes before start — hit it now
      handleActionPerUserType();
      return;
    }

    // Otherwise, wait until that time
    const timeoutId = setTimeout(() => {
      handleActionPerUserType();
    }, waitTime);

    return () => clearTimeout(timeoutId); // Cleanup if user leaves the page
  }, [webinarDetails?.startTime, webinarId, isHost, attendeeDetails, userId, webinarDetails?.status]);

  return {
    isAttendee,
    haveActiveTimer,
    isLoading,
    setHaveActiveTimer,
    isWebinarStartedByHost,
    webinarDetails,
    isJoinedRoom,
  };
};

export default useWebinarJoin;
